/**
 * Constants for the Orders listing.
 *
 * The KPI tiles + filter option lists are lifted verbatim from the
 * standalone HTML prototype (`bundle-ba195e24` + `bundle-57193aac`).
 * The prototype's mock rows live in `__fixtures__/orderRows.ts` (test-only).
 */
import { SHOPIFY_STORE_IDS } from 'utils/constructShopifyUrl';
import type { OrderPaymentStatus } from './types/order';
import { KPI_EMPTY_VALUE } from 'utils/kpiFormat';

export const ORDERS_PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
export const ORDERS_DEFAULT_PAGE_SIZE = 100;

/**
 * Order fulfillment status options for the Status dropdown.
 *
 * Values mirror the Vue ERP's status taxonomy (`orderFulfillmentStatus`
 * field on the backend). The `MV_*` family represents "Missing Variant"
 * subtypes — orders that can't be fulfilled because a required variant
 * attribute is missing (Address / Price / Product / PFAS compliance /
 * required-field check). Wire values are UPPER_SNAKE to match the
 * backend enum convention; the label is the rendered text.
 */
/**
 * Wire values match Vue's `FULFILLMENT_STATUS` map keys exactly
 * (`RW-ERP-Service/src/types/orders/order-constants.ts:87-101`). The
 * backend ships `orderFulfillmentStatus` in this casing — using
 * UPPER_SNAKE_CASE here meant the row-level colour / filter lookup
 * failed for every status and the badge fell back to the white default.
 *
 * No `all` sentinel — the multi-select represents "no filter" as an
 * empty selected array. Adding an `all` row would force every consumer
 * to special-case it.
 */
// `labelKey` carries the i18n key; `label` stays as the English
// fallback the render sites pass to `t(labelKey, label)`. The MV_*
// entries keep their technical codes as display text in every locale —
// they're backend enum names the fulfillment team searches by.
/** Wire label for a canceled order — shared by the row-actions menu
 *  and the slide-over's Share Invoice gate. */
export const ORDER_STATUS_CANCELED = 'Canceled';
/** Wire label of the fulfilment success state (Vue `FULFILLMENT_STATUS`) —
 *  the KPI fallback counts every OTHER status as unfulfilled. */
export const ORDER_STATUS_FULFILLED = 'Fulfilled';
/** Wire label (uppercase) for a canceled SHIPMENT — distinct from
 *  `ORDER_STATUS_CANCELED` ('Canceled', title case) because the two
 *  domains ship different casings on the wire. Used by the expanded
 *  row to hide canceled shipments (Vue `OrdersCollapse.vue` parity). */
export const SHIPMENT_STATUS_CANCELED = 'CANCELED';

export const STATUS_OPTIONS = [
  { value: 'Canceled', label: 'Canceled', labelKey: 'orders.status.canceled' },
  { value: 'Fulfilled', label: 'Fulfilled', labelKey: 'orders.status.fulfilled' },
  { value: 'MV_Address', label: 'MV_Address', labelKey: 'orders.status.mvAddress' },
  { value: 'MV_PFAS', label: 'MV_PFAS', labelKey: 'orders.status.mvPfas' },
  { value: 'MV_Price', label: 'MV_Price', labelKey: 'orders.status.mvPrice' },
  { value: 'MV_Product', label: 'MV_Product', labelKey: 'orders.status.mvProduct' },
  { value: 'MV_Required', label: 'MV_Required', labelKey: 'orders.status.mvRequired' },
  { value: 'MV_Total', label: 'MV_Total', labelKey: 'orders.status.mvTotal' },
  { value: 'On hold', label: 'On hold', labelKey: 'orders.status.onHold' },
  {
    value: 'Partially Fulfilled',
    label: 'Partially Fulfilled',
    labelKey: 'orders.status.partiallyFulfilled',
  },
  { value: 'Scheduled', label: 'Scheduled', labelKey: 'orders.status.scheduled' },
  { value: 'Unfulfilled', label: 'Unfulfilled', labelKey: 'orders.status.unfulfilled' },
] as const;

export const PAYMENT_STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses', labelKey: 'orders.paymentFilter.all' },
  { value: 'paid', label: 'Paid', labelKey: 'orders.paymentFilter.paid' },
  { value: 'pending', label: 'Pending', labelKey: 'orders.paymentFilter.pending' },
] as const;

/**
 * Dubai (AE) Shopify store IDs — MAIN_AE + PUNCHOUT_AE. Vue excludes
 * these from every order listing except the Dubai-store route via a
 * negated `storeRefId among [...]` filter (`OrderView.vue:307-315`;
 * IDs from `SHOPIFY_STORE_IDS.IDS` in
 * `types/constants/shopify-ecom-constant.ts`). The React app has no
 * Dubai-store route yet, so `buildOrdersSearchRequest` applies the
 * exclusion unconditionally.
 */
export const DUBAI_STORE_IDS = [
  SHOPIFY_STORE_IDS.IDS.MAIN_AE,
  SHOPIFY_STORE_IDS.IDS.PUNCHOUT_AE,
] as const;

/**
 * Vue-app route for the "Create CS Return" screen (not yet migrated to
 * React — the row-action opens it on the Vue origin). Mirrors
 * `CREATE_CS_RETURN_ROUTE` in
 * `RW-ERP-Service/src/types/returns/returns-constants.ts:28` and is also
 * the key `hasPermissionForRoute` resolves in `routePermissions.ts`.
 */
export const CREATE_CS_RETURN_ROUTE = '/returns/create/customer-support';

/**
 * Stores whose orders can raise a CS return from the listing's action
 * menu — MAIN_US + PUNCHOUT_US. Vue gates the menu item on
 * `storeRefId` matching these (`OrderListingScreen.vue:239-243`; IDs
 * from `SHOPIFY_STORE_IDS.IDS` in
 * `types/constants/shopify-ecom-constant.ts`).
 */
export const CS_RETURN_STORE_IDS = [
  SHOPIFY_STORE_IDS.IDS.MAIN_US,
  SHOPIFY_STORE_IDS.IDS.PUNCHOUT_US,
] as const;

/** Query-param the Vue "Create CS Return" screen reads the order number
 *  from (`?order-id=<orderNumber>`). */
export const CS_RETURN_ORDER_ID_PARAM = 'order-id';

// ── Export (breadcrumb-bar button) ──────────────────────────────────
// Vue source-of-truth: `RW-ERP-Service/src/types/orders/order-constants.ts`
// (`ORDER_EXPORT_FILE_NAME`, `ORDER_EXPORT_MAX_RANGE_DAYS`,
// `ORDER_EXPORT_ATTRIBUTES`). The attribute CODES are fixed here and
// resolved to `{ attributeCode, attributeId }` pairs at runtime from the
// Orders domain metadata — ids differ per environment, so they must not
// be hardcoded.
export const ORDER_EXPORT_FILE_NAME = 'order-details-export';

/** Maximum date-range span (in days) allowed for an orders export. */
export const ORDER_EXPORT_MAX_RANGE_DAYS = 180;

export const ORDER_EXPORT_ATTRIBUTES = [
  'shippingMethod',
  'orderFinancialStatus',
  'ecomOrderNumber',
  'orderFulfillmentStatus',
  'grandTotal',
  'poNumber',
  'customerType',
  'customerFirstName',
  'customerLastName',
  // Added 2026-09-23 (missing-fields report). Codes verified verbatim
  // against the live orders-domain metadata (261 attributes) — a code
  // the metadata does not carry is silently skipped by the resolver,
  // so an exact match is what makes these actually export. Note this
  // list now leads Vue's ORDER_EXPORT_ATTRIBUTES (order-constants.ts),
  // which still ends at customerLastName.
  'salesIAccountNumber',
  'hubspotCompanyId',
] as const;


// ── Date / time conventions shared by the listing and the detail sheet ──

/** Locale every Orders date/time formatter renders in (Vue parity). */
export const ORDERS_DATE_LOCALE = 'en-US';

/**
 * IANA zone the backend's wall-clock timestamps are expressed in. Most
 * formatters must NOT pass it to `Intl` (the strings are reformatted
 * verbatim — see `mapOrders.ts` `DATE_FMT`); it is for the two places
 * that genuinely convert an instant: `centralNow()` in the Timeline tab
 * and the estimated-delivery formatter in `OrderDetailsBody`.
 */
export const ORDERS_TIME_ZONE = 'America/Chicago';

/**
 * Zone LABEL is pinned to "CST" year-round — Vue parity:
 * `HkDateTime.vue:38` renders the literal string `'CST'`, never a
 * daylight-aware name (`timeZoneName: 'short'` would render "CDT" for
 * summer dates and disagree with the Vue app).
 */
export const ORDERS_TIMEZONE_LABEL = 'CST';

/** Placeholder for an absent display value (em dash). */
export const ORDERS_EMPTY_VALUE = KPI_EMPTY_VALUE;

/**
 * The row mappers' own "not available" sentinel (`mapOrders` emits it
 * for absent wire values). A WIRE-side placeholder — display code
 * treats it as absent; it is NOT the display em dash above.
 */
export const ORDERS_NA_SENTINEL = 'N/A';

/** Payment status a not-yet-paid sales order carries (the listing's
 *  `pay` column vocabulary). */
export const ORDER_PAYMENT_STATUS_PENDING: OrderPaymentStatus = 'Pending';

/** Interpunct separating short facts on one line ("2 SKUs · 5 units"). */
export const ORDERS_DOT_SEPARATOR = '·';

/** Multiplication glyph between a quantity and its unit figure. */
export const ORDERS_TIMES_GLYPH = '×';

/**
 * Query-param names of the order-detail deep link
 * (`/orders/sales-orders/all?orderRefId=…&orderNumber=…`) — written into
 * notification links (`sendStagedOrderNotification`) and read back by the
 * Orders page, which opens the slide-over for that order on load.
 */
export const ORDER_DETAIL_DEEPLINK_PARAMS = {
  refId: 'orderRefId',
  orderNumber: 'orderNumber',
} as const;
